'use client';

/**
 * CryptoWalletsCard - Saved External Crypto Wallets Card (Liquid Glass Hero)
 * 
 * Displays user's saved external crypto wallet addresses for quick withdrawals.
 * These are stored locally (address book) - NOT synced with Bridge API.
 * 
 * Uses Liquid Glass Hero card pattern consistent with BankAccountsCard.
 * 
 * @see useSavedAddresses hook
 * @see PRD-WALLET-HUB
 */

import { NetworkIcon } from '@/components/NetworkIcons';
import { CurrencyIcon } from '@/components/ui/CurrencyIcon';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SFIcon } from '@/components/ui/sf-icon';
import { Skeleton } from '@/components/ui/skeleton';
import { Heading, Text } from '@/components/ui/typography';
import {
  SavedAddress,
  useDeleteSavedAddress,
  useSavedAddresses
} from '@/hooks/useSavedAddresses';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState } from '@supabank/ui';
import {
  MoreVertical,
  Trash2
} from 'lucide-react';
import { useState } from 'react';
import { toast } from '@/hooks/use-toast';
import { AddSavedAddressDialog } from './AddSavedAddressDialog';

interface CryptoWalletsCardProps {
  onSelectWallet?: (wallet: SavedAddress) => void;
}

export function CryptoWalletsCard({ onSelectWallet }: CryptoWalletsCardProps) {
  // State
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SavedAddress | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Data
  const { data: wallets, isLoading, refetch, isRefetching } = useSavedAddresses();
  const deleteAddress = useDeleteSavedAddress();

  // Truncate address for display
  const truncateAddress = (address: string) => {
    if (!address || address.length < 16) return address;
    return `${address.slice(0, 8)}...${address.slice(-6)}`;
  };

  // Copy address to clipboard
  const handleCopy = async (wallet: SavedAddress) => {
    try {
      await navigator.clipboard.writeText(wallet.address);
      setCopiedId(wallet.id);
      toast.success('Address copied');
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  // Delete address
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAddress.mutateAsync(deleteTarget.id);
      setDeleteTarget(null);
    } catch {
      // Error handled by mutation
    }
  };

  return (
    <>
      <section
        className={cn(
          // Liquid Glass Surface tier (Hero)
          "rounded-[28px]",
          "bg-gradient-to-br from-white/90 via-white/85 to-white/90",
          "dark:from-slate-900/85 dark:via-slate-800/75 dark:to-slate-900/85",
          "backdrop-blur-xl backdrop-saturate-[180%]",
          "dark:backdrop-saturate-150 dark:brightness-90",
          "border border-white/30 dark:border-white/15",
          "shadow-[0_8px_32px_hsl(220_15%_5%/0.15)]",
          "dark:shadow-[0_8px_32px_hsl(0_0%_0%/0.5)]",
          // Layout
          "p-6",
          // Motion
          semanticTokens.motion.standard
        )}
        aria-label="Saved Crypto Wallets"
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={cn(
              'p-2.5 rounded-xl',
              'bg-orange-100 dark:bg-orange-900/30'
            )}>
              <SFIcon icon={sfIcons.wallet} className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Heading level={3}>Crypto Wallets</Heading>
                {wallets && wallets.length > 0 && (
                  <span className={cn(
                    'text-xs px-2 py-0.5 rounded-full',
                    'bg-muted text-muted-foreground'
                  )}>
                    {wallets.length}
                  </span>
                )}
              </div>
              <Text variant="caption" color="muted">
                Your saved addresses for quick withdrawals
              </Text>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => refetch()}
              disabled={isRefetching}
              className={cn(
                "min-h-[44px] min-w-[44px] rounded-xl",
                semanticTokens.motion.standard
              )}
              aria-label="Refresh saved wallets"
            >
              <SFIcon icon={sfIcons.refresh} className={cn("h-5 w-5", isRefetching && "animate-spin")} />
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAddDialog(true)}
              className={cn(
                "min-h-[44px] rounded-xl gap-2",
                semanticTokens.motion.standard
              )}
            >
              <SFIcon icon={sfIcons.add} className="h-4 w-4" />
              <span className="hidden sm:inline">Add Wallet</span>
            </Button>
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2].map((i) => (
              <Skeleton key={i} className={cn("h-20 w-full", semanticTokens.radius.lg)} />
            ))}
          </div>
        ) : !wallets || wallets.length === 0 ? (
          <EmptyState
            icon={() => <SFIcon icon={sfIcons.wallet} />}
            title="No saved wallets"
            description="Add your external crypto wallet addresses for quick withdrawals"
            action={{
              label: 'Add Wallet',
              onClick: () => setShowAddDialog(true),
            }}
          />
        ) : (
          <div className="grid gap-3 sm:grid-cols-2">
            {wallets.map((wallet) => (
              <div
                key={wallet.id}
                className={cn(
                  "flex items-center gap-3 p-4 rounded-xl group",
                  "bg-card/50 hover:bg-card/80",
                  "border border-border/50 hover:border-border",
                  semanticTokens.motion.standard,
                  "focus-visible:ring-2 focus-visible:ring-primary",
                  onSelectWallet && "cursor-pointer"
                )}
                onClick={() => onSelectWallet?.(wallet)}
                role={onSelectWallet ? "button" : undefined}
                tabIndex={onSelectWallet ? 0 : undefined}
                onKeyDown={(e) => e.key === 'Enter' && onSelectWallet?.(wallet)}
              >
                <div className="relative flex-shrink-0">
                  <NetworkIcon network={wallet.chain} className="h-10 w-10" />
                  <div className="absolute -bottom-1 -right-1 bg-background rounded-full p-0.5">
                    <CurrencyIcon currency={wallet.currency || 'usdc'} size="xs" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Text variant="md" className="font-medium truncate">
                      {wallet.label}
                    </Text>
                    <span className={cn(
                      'text-xs px-1.5 py-0.5 rounded font-medium',
                      'bg-primary/10 text-primary'
                    )}>
                      {(wallet.currency || 'usdc').toUpperCase()}
                    </span>
                    {wallet.verified && (
                      <span className={cn(
                        'text-xs px-1.5 py-0.5 rounded font-medium',
                        'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
                        'flex items-center gap-1'
                      )}>
                        <SFIcon icon={sfIcons.success} className="h-3 w-3" />
                        Verified
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5">
                    <code className={cn(
                      semanticTokens.typography.caption,
                      semanticTokens.fg.muted,
                      "font-mono"
                    )}>
                      {truncateAddress(wallet.address)}
                    </code>
                    <span className={cn(
                      'text-xs px-1.5 py-0.5 rounded',
                      'bg-muted/50 text-muted-foreground capitalize'
                    )}>
                      {wallet.chain}
                    </span>
                  </div>
                </div>

                {/* Actions Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        "min-h-[36px] min-w-[36px] rounded-lg opacity-0 group-hover:opacity-100",
                        semanticTokens.motion.standard
                      )}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleCopy(wallet); }}>
                      {copiedId === wallet.id ? (
                        <SFIcon icon={sfIcons.success} className="h-4 w-4 mr-2 text-green-500" />
                      ) : (
                        <SFIcon icon={sfIcons.copy} className="h-4 w-4 mr-2" />
                      )}
                      Copy Address
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(getExplorerUrl(wallet.chain, wallet.address), '_blank');
                      }}
                    >
                      <SFIcon icon={sfIcons.externalLink} className="h-4 w-4 mr-2" />
                      View on Explorer
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={(e) => { e.stopPropagation(); setDeleteTarget(wallet); }}
                      className="text-red-600 focus:text-red-600"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Remove
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Add Dialog */}
      <AddSavedAddressDialog
        open={showAddDialog}
        onClose={() => setShowAddDialog(false)}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove saved wallet?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove "{deleteTarget?.label}" from your saved wallets.
              You can add it again later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleteAddress.isPending ? 'Removing...' : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

// Helper: Get blockchain explorer URL
function getExplorerUrl(chain: string, address: string): string {
  const explorers: Record<string, string> = {
    ethereum: `https://etherscan.io/address/${address}`,
    base: `https://basescan.org/address/${address}`,
    solana: `https://solscan.io/account/${address}`,
    polygon: `https://polygonscan.com/address/${address}`,
    arbitrum: `https://arbiscan.io/address/${address}`,
    optimism: `https://optimistic.etherscan.io/address/${address}`,
    avalanche: `https://snowtrace.io/address/${address}`,
    tron: `https://tronscan.org/#/address/${address}`,
    stellar: `https://stellar.expert/explorer/public/account/${address}`,
  };
  return explorers[chain] || `https://etherscan.io/address/${address}`;
}

// Re-export types for convenience
export type { SavedAddress };
